import { oc } from "@orpc/contract";
import { z } from "zod";
import { CredentialByIdInputSchema } from "./credentials.contract";

/**
 * Machine-to-machine credential resolution for the Python ai-service's
 * `ProductionCredentialResolver` (AI-246) — analogous to `sandboxes.*`: the
 * whole `credentialsInternal.*` namespace is authenticated by a shared service
 * token (see the `/credentialsInternal/` gate in the RPC express adapter), not
 * a user session, so the caller declares its `organizationId` on every call.
 * Deliberately a SEPARATE token from `aiService.serviceToken` — least
 * privilege, so a leaked ai-service token can't also unlock secrets.
 *
 * Never reuse this namespace for a user-facing endpoint — it returns the
 * decrypted secret value.
 */

export const ResolveCredentialInternalResponseSchema = z
  .object({
    secret: z.string(),
    metadata: z.record(z.string(), z.unknown()).nullable(),
  })
  .nullable();

/**
 * Resolve a live OTP code for an agent OTP target (TOTP generated locally, or
 * an SMS code awaited from the inbound-SMS table). Same service-token gate as
 * `resolveById` — the code is a live secret, so it belongs in this namespace.
 * Used by the Python operator's hardcoded AVD login to clear an MFA challenge.
 */
export const ResolveOtpInternalInputSchema = z.object({
  organizationId: z.string().uuid(),
  // Not a UUID: an OTP target can be a TOTP credential id or the org's SMS
  // number identifier — keep it opaque.
  otpTargetId: z.string().min(1),
});

export const ResolveOtpInternalResponseSchema = z.object({
  code: z.string(),
  remainingSeconds: z.number(),
});

/**
 * Playwright storage state (cookies + per-origin localStorage). Mirrors
 * `PlaywrightStorageState` in the backend's AgentAuthStateService exactly, so the
 * handler needs no cast. Used to persist/reuse an Azure/AVD session so the
 * operator's login can skip email/password/MFA on later runs.
 */
export const PlaywrightStorageStateSchema = z.object({
  cookies: z.array(
    z.object({
      name: z.string(),
      value: z.string(),
      domain: z.string(),
      path: z.string(),
      expires: z.number(),
      httpOnly: z.boolean(),
      secure: z.boolean(),
      sameSite: z.enum(["Strict", "Lax", "None"]),
    }),
  ),
  origins: z.array(
    z.object({
      origin: z.string(),
      localStorage: z.array(z.object({ name: z.string(), value: z.string() })),
    }),
  ),
});

export const GetAuthStateInternalInputSchema = z.object({
  organizationId: z.string().uuid(),
  // The agent definition id the auth-state is keyed by.
  targetId: z.string().uuid(),
  // The account the login will sign in as. A saved session for any other account is expired
  // and null returned, so the login starts fresh instead of reusing the wrong session.
  username: z.string().min(1).optional(),
});

export const GetAuthStateInternalResponseSchema = z
  .object({ storageState: PlaywrightStorageStateSchema })
  .nullable();

export const StoreAuthStateInternalInputSchema = z.object({
  organizationId: z.string().uuid(),
  targetId: z.string().uuid(),
  storageState: PlaywrightStorageStateSchema,
  targetUrl: z.string(),
  name: z.string(),
  username: z.string().optional(),
  otpTargetId: z.string().optional(),
});

export const credentialsInternalContracts = {
  recordVerifiedLogin: oc
    .input(
      z.object({
        organizationId: z.uuid(),
        credentialId: z.uuid(),
        agentRunId: z.uuid(),
      }),
    )
    .output(z.object({ ok: z.boolean() }))
    .route({
      method: "POST",
      summary: "Record verified credential authentication",
      description:
        "Service-token only. Resets a credential's rejection streak after the operator observed a successful sign-in. Scoped to the run's organization; does not reactivate resources.",
    }),
  resolveById: oc
    .input(CredentialByIdInputSchema)
    .output(ResolveCredentialInternalResponseSchema)
    .route({
      method: "POST",
      summary: "Resolve a credential secret for the AI service",
      description:
        "Returns the decrypted secret and metadata for one credential, or null. Service-token only; never user-facing.",
    }),
  resolveOtp: oc
    .input(ResolveOtpInternalInputSchema)
    .output(ResolveOtpInternalResponseSchema)
    .route({
      method: "POST",
      summary: "Get a live one-time code for an OTP target",
      description:
        "Generates a TOTP code or waits for an inbound SMS code so an automated login can clear MFA. Service-token only.",
    }),
  // Auth-state reuse for the operator's AVD login: persist the Azure session so
  // later runs skip email/password/MFA. Same service-token gate.
  getAuthState: oc
    .input(GetAuthStateInternalInputSchema)
    .output(GetAuthStateInternalResponseSchema)
    .route({
      method: "POST",
      summary: "Get a saved browser login session",
      description:
        "Returns the stored cookies and local storage for an agent definition so automated logins can skip sign-in and MFA, or null. Service-token only.",
    }),
  storeAuthState: oc
    .input(StoreAuthStateInternalInputSchema)
    .output(z.object({ ok: z.boolean() }))
    .route({
      method: "POST",
      summary: "Save a browser login session",
      description:
        "Stores cookies and local storage from a successful automated login for reuse on later runs of the agent. Service-token only.",
    }),
} as const;

export type ResolveCredentialInternalResponse = z.infer<
  typeof ResolveCredentialInternalResponseSchema
>;

export type ResolveOtpInternalResponse = z.infer<
  typeof ResolveOtpInternalResponseSchema
>;
